/**
 * Preferência por-usuário de quais seções da tela de Atendimento (aba
 * "Entradas" da ficha do paciente) começam EXPANDIDAS ao abrir a página.
 *
 * Persistida em localStorage keyed pelo uid — mesma família das prefs
 * pessoais do tema (`dratento-theme`) e do tour. NÃO vai pro Firestore
 * porque é preferência de UI individual, não config da clínica.
 *
 * Fonte única de verdade: as duas pontas que editam — o chevron inline
 * no `PatientSessionPanel` e a aba "Tela de Atendimento" das Configurações
 * (`SessionLayoutSettingsTab`) — leem/gravam ESTA mesma chave. Por isso
 * recolher uma seção na própria tela também vira o novo "padrão de
 * abertura".
 */

export type SessionSectionKey =
  | 'accompanist'
  | 'caseSummary'
  | 'recording'
  | 'observations'
  | 'encounterExams'
  | 'attachments'
  | 'manualDocs'
  | 'extractedText';

export interface SessionSectionDef {
  key: SessionSectionKey;
  label: string;
  hint: string;
}

/** Ordem = ordem de exibição na tela e na aba de Configurações. */
export const SESSION_SECTIONS: SessionSectionDef[] = [
  { key: 'accompanist', label: 'Acompanhante do dia', hint: 'Vincula um paciente cadastrado como acompanhante da consulta.' },
  { key: 'caseSummary', label: 'Resumo do caso', hint: 'Resumo das consultas anteriores gerado por IA.' },
  { key: 'encounterExams', label: 'Exames do atendimento', hint: 'Painel modular (refração, PIO, biometria…). Primeiro a preencher — ficha base.' },
  { key: 'recording', label: 'Gravar áudio', hint: 'Captura de áudio do atendimento.' },
  { key: 'observations', label: 'Observações do médico', hint: 'Texto livre / contexto adicional para a IA.' },
  { key: 'attachments', label: 'Anexos (PDF e Imagem)', hint: 'Laudos, exames anteriores e fotos clínicas.' },
  { key: 'manualDocs', label: 'Documentos manuais', hint: 'Receitas, atestados, laudos, pedidos de exame…' },
  { key: 'extractedText', label: 'Texto extraído', hint: 'Markdown transcrito/formatado pela IA.' },
];

export type SessionSectionPrefs = Record<SessionSectionKey, boolean>;

/** Default: tudo expandido — preserva o comportamento atual da tela. */
export const DEFAULT_SECTION_PREFS: SessionSectionPrefs = {
  accompanist: true,
  caseSummary: true,
  recording: true,
  observations: true,
  encounterExams: true,
  attachments: true,
  manualDocs: true,
  extractedText: true,
};

const storageKey = (uid: string) => `dratento-session-sections:${uid}`;

/** Lê as prefs do usuário; faz merge com o default pra tolerar chaves novas. */
export function loadSectionPrefs(uid: string | null | undefined): SessionSectionPrefs {
  if (!uid) return { ...DEFAULT_SECTION_PREFS };
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return { ...DEFAULT_SECTION_PREFS };
    const parsed = JSON.parse(raw) as Partial<SessionSectionPrefs>;
    return { ...DEFAULT_SECTION_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_SECTION_PREFS };
  }
}

export function saveSectionPrefs(
  uid: string | null | undefined,
  prefs: SessionSectionPrefs,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify(prefs));
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Atalhos laterais da tela de PRONTUÁRIO (gaveta retrátil à direita)
// ═══════════════════════════════════════════════════════════════════

/**
 * Seções da tela de Atendimento que podem aparecer como BOTÕES LATERAIS
 * na aba Prontuário — cada botão abre a seção numa gaveta flutuante sem
 * sair do histórico clínico. Subconjunto de `SessionSectionKey` (mesmas
 * chaves), escolhido pelo médico: observações, exames do atendimento,
 * anexos, documentos manuais e texto extraído.
 */
export type ProntuarioRailKey =
  | 'observations'
  | 'encounterExams'
  | 'attachments'
  | 'manualDocs'
  | 'extractedText';

export interface ProntuarioRailDef {
  key: ProntuarioRailKey;
  label: string;
  hint: string;
}

/** Ordem = ordem dos botões na borda da tela e na aba de Configurações. */
export const PRONTUARIO_RAIL_SECTIONS: ProntuarioRailDef[] = [
  { key: 'observations', label: 'Observações do médico', hint: 'Texto livre — salvar no prontuário ou imprimir.' },
  { key: 'encounterExams', label: 'Exames do atendimento', hint: 'Painel modular (refração, PIO, biometria…).' },
  { key: 'attachments', label: 'Anexos (PDF e Imagem)', hint: 'Laudos, exames anteriores e fotos clínicas.' },
  { key: 'manualDocs', label: 'Documentos manuais', hint: 'Receitas, atestados, laudos, pedidos de exame…' },
  { key: 'extractedText', label: 'Texto extraído', hint: 'Markdown transcrito/formatado pela IA.' },
];

export type ProntuarioRailPrefs = Record<ProntuarioRailKey, boolean>;

/** Default: todos os botões visíveis — o médico desliga o que não usa. */
export const DEFAULT_RAIL_PREFS: ProntuarioRailPrefs = {
  observations: true,
  encounterExams: true,
  attachments: true,
  manualDocs: true,
  extractedText: true,
};

const railStorageKey = (uid: string) => `dratento-prontuario-rail:${uid}`;

/** Lê as prefs dos atalhos do Prontuário; merge com o default pra tolerar chaves novas. */
export function loadRailPrefs(uid: string | null | undefined): ProntuarioRailPrefs {
  if (!uid) return { ...DEFAULT_RAIL_PREFS };
  try {
    const raw = localStorage.getItem(railStorageKey(uid));
    if (!raw) return { ...DEFAULT_RAIL_PREFS };
    const parsed = JSON.parse(raw) as Partial<ProntuarioRailPrefs>;
    return { ...DEFAULT_RAIL_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_RAIL_PREFS };
  }
}

export function saveRailPrefs(
  uid: string | null | undefined,
  prefs: ProntuarioRailPrefs,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(railStorageKey(uid), JSON.stringify(prefs));
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Barra de ferramentas FIXA do topo do bloco geral (aba Prontuário, ≥xl)
// ═══════════════════════════════════════════════════════════════════

/**
 * Catálogo de TODAS as ações que podem aparecer na barra fixa do topo do
 * "bloco geral" da aba Prontuário (desktop). O médico escolhe quais ficam
 * fixadas (visíveis) e quais vão pro botão "+ Mais". O ícone e o que cada
 * ação dispara vivem no componente `ProntuarioToolbar` — aqui ficam só os
 * metadados puros (label, categoria) pra não acoplar lucide/React a um util.
 */
export type ProntuarioToolbarKey =
  | 'novaEvolucao'
  | 'gravar'
  | 'multimodal'
  | 'extrairDados'
  | 'docInstrucoesIA'
  | 'docsManuais'
  | 'docTexto'
  | 'docSolicitacoes'
  | 'docOculos'
  | 'docReceita'
  | 'docLaudoSus'
  | 'docImpressos'
  | 'examesAtendimento'
  | 'anexos'
  | 'citarNoChat';

export type ProntuarioToolbarCategory = 'criar' | 'ia' | 'examesAnexos' | 'comunicar';

export interface ProntuarioToolbarDef {
  key: ProntuarioToolbarKey;
  label: string;
  category: ProntuarioToolbarCategory;
}

/** Rótulos legíveis das categorias (cabeçalhos no painel "Personalizar"). */
export const PRONTUARIO_TOOLBAR_CATEGORY_LABELS: Record<ProntuarioToolbarCategory, string> = {
  criar: 'Criar',
  ia: 'IA',
  examesAnexos: 'Exames & anexos',
  comunicar: 'Equipe',
};

/** Catálogo completo, na ordem natural de exibição no painel "Personalizar".
 *  CONSOLIDADO (espelha a grade "Documentos manuais"): Documento de Texto
 *  reúne atestado/laudo/declaração/encaminhamento/texto simples; Solicitações
 *  reúne exames + cirurgias; Anexos & Imagens reúne os 3 botões de anexar.
 *  "Documentos manuais" abre a própria grade de cards. */
export const PRONTUARIO_TOOLBAR_ACTIONS: ProntuarioToolbarDef[] = [
  { key: 'novaEvolucao', label: 'Nova evolução', category: 'criar' },
  { key: 'docsManuais', label: 'Documentos manuais', category: 'criar' },
  { key: 'docTexto', label: 'Documento de Texto', category: 'criar' },
  { key: 'docSolicitacoes', label: 'Solicitações', category: 'criar' },
  { key: 'docOculos', label: 'Receita de óculos', category: 'criar' },
  { key: 'docReceita', label: 'Receita de medicamentos', category: 'criar' },
  { key: 'docLaudoSus', label: 'Laudos SUS', category: 'criar' },
  { key: 'docImpressos', label: 'Impressos / Orientações', category: 'criar' },
  { key: 'extrairDados', label: 'Extrair dados', category: 'ia' },
  { key: 'gravar', label: 'Gravar', category: 'ia' },
  { key: 'multimodal', label: 'Análise multimodal', category: 'ia' },
  { key: 'docInstrucoesIA', label: 'Instruções para IA', category: 'ia' },
  { key: 'examesAtendimento', label: 'Exames do atendimento', category: 'examesAnexos' },
  { key: 'anexos', label: 'Anexos & Imagens', category: 'examesAnexos' },
  // Abre o chat interno com este paciente já anexado (vínculo clicável).
  { key: 'citarNoChat', label: 'Enviar no chat', category: 'comunicar' },
];

/**
 * Migração das chaves pré-consolidação → botão consolidado equivalente.
 * Preserva a personalização salva do médico: quem tinha "Atestado" fixado
 * passa a ter "Documento de Texto" na mesma posição (duplicatas colapsam).
 */
const TOOLBAR_KEY_MIGRATION: Record<string, ProntuarioToolbarKey> = {
  docAtestado: 'docTexto',
  docEncaminhamento: 'docTexto',
  docDeclaracao: 'docTexto',
  docTextoSimples: 'docTexto',
  docExames: 'docSolicitacoes',
  docCirurgias: 'docSolicitacoes',
  anexarImagens: 'anexos',
  anexarPdf: 'anexos',
  anexarImagemIA: 'anexos',
};

const TOOLBAR_KEYS = new Set<string>(PRONTUARIO_TOOLBAR_ACTIONS.map((a) => a.key));

/**
 * Modo de exibição dos botões da barra:
 * - `full`: ícone + rótulo (padrão);
 * - `icon`: só o ícone (barra compacta — o rótulo vira tooltip).
 */
export type ProntuarioToolbarDisplay = 'full' | 'icon';

export const PRONTUARIO_TOOLBAR_DISPLAY_OPTIONS: Array<{
  value: ProntuarioToolbarDisplay;
  label: string;
  hint: string;
}> = [
  {
    value: 'full',
    label: 'Ícone + texto (padrão)',
    hint: 'Cada botão mostra o ícone e o nome da ação. Mais fácil de reconhecer, ocupa mais linhas.',
  },
  {
    value: 'icon',
    label: 'Apenas ícone',
    hint: 'Barra compacta: só os ícones, com o nome da ação aparecendo ao passar o mouse. Sobra mais espaço para o prontuário.',
  },
];

/**
 * Config da barra: a lista `pinned` é, ao mesmo tempo, QUAIS botões aparecem
 * na barra E a ordem deles (arrastar reordena). Tudo que não está em `pinned`
 * cai no menu "+ Mais", na ordem do catálogo. `display` define se os botões
 * fixados saem com rótulo ao lado do ícone ou só com o ícone.
 */
export interface ProntuarioToolbarConfig {
  pinned: ProntuarioToolbarKey[];
  display: ProntuarioToolbarDisplay;
}

/** Default: um subconjunto enxuto das ações mais frequentes, com rótulo. */
export const DEFAULT_TOOLBAR_CONFIG: ProntuarioToolbarConfig = {
  display: 'full',
  pinned: [
    'novaEvolucao',
    'gravar',
    'extrairDados',
    'docsManuais',
    'docTexto',
    'docSolicitacoes',
    'docOculos',
    'docReceita',
    'docImpressos',
    'examesAtendimento',
    'anexos',
    'citarNoChat',
  ],
};

const toolbarStorageKey = (uid: string) => `dratento-prontuario-toolbar:${uid}`;

/** Lê a config da barra; descarta chaves inválidas/duplicadas e cai no default.
 *  Configs salvas antes da consolidação (sem `v`) passam pela migração de
 *  chaves e ganham o botão "Documentos manuais" uma única vez — depois disso
 *  o médico pode tirá-lo da barra que ele não volta sozinho (v ≥ 2). */
export function loadToolbarConfig(uid: string | null | undefined): ProntuarioToolbarConfig {
  const fallback = (): ProntuarioToolbarConfig => ({
    pinned: [...DEFAULT_TOOLBAR_CONFIG.pinned],
    display: DEFAULT_TOOLBAR_CONFIG.display,
  });
  if (!uid) return fallback();
  try {
    const raw = localStorage.getItem(toolbarStorageKey(uid));
    if (!raw) return fallback();
    const parsed = JSON.parse(raw) as Partial<ProntuarioToolbarConfig> & { v?: number };
    if (!Array.isArray(parsed.pinned)) return fallback();
    // Configs salvas antes do seletor de exibição não têm `display` → padrão.
    const display: ProntuarioToolbarDisplay = parsed.display === 'icon' ? 'icon' : 'full';
    const version = parsed.v ?? 1;
    const legacy = version < 2;
    const seen = new Set<string>();
    const pinned = parsed.pinned
      .map((k) => (legacy && typeof k === 'string' ? (TOOLBAR_KEY_MIGRATION[k] ?? k) : k))
      .filter(
        (k): k is ProntuarioToolbarKey =>
          typeof k === 'string' && TOOLBAR_KEYS.has(k) && !seen.has(k) && (seen.add(k), true),
      );
    if (legacy && !seen.has('docsManuais')) pinned.push('docsManuais');
    // v3 trouxe o "Enviar no chat" — entra na barra uma única vez.
    if (version < 3 && !seen.has('citarNoChat')) pinned.push('citarNoChat');
    return { pinned, display };
  } catch {
    return fallback();
  }
}

export function saveToolbarConfig(
  uid: string | null | undefined,
  config: ProntuarioToolbarConfig,
): void {
  if (!uid) return;
  try {
    // `v` marca até onde a config já migrou — o load não migra de novo.
    localStorage.setItem(toolbarStorageKey(uid), JSON.stringify({ ...config, v: 3 }));
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Toggle "bloco geral fixo" ↔ "rolagem livre" (aba Prontuário, ≥xl)
// ═══════════════════════════════════════════════════════════════════

const fixedLayoutStorageKey = (uid: string) => `dratento-prontuario-fixed:${uid}`;

/** Default: TRUE (bloco preso, só o miolo rola). Falsy/erro → default. */
export function loadProntuarioFixed(uid: string | null | undefined): boolean {
  if (!uid) return true;
  try {
    const raw = localStorage.getItem(fixedLayoutStorageKey(uid));
    if (raw == null) return true;
    return JSON.parse(raw) === true;
  } catch {
    return true;
  }
}

export function saveProntuarioFixed(
  uid: string | null | undefined,
  fixed: boolean,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(fixedLayoutStorageKey(uid), JSON.stringify(fixed));
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Toggle "página fixa" ↔ "rolagem livre" GENÉRICO (Agenda, Pacientes,
// Financeiro…). Mesmo comportamento do prontuário, mas com uma chave por
// página, pra cada tela lembrar a escolha do médico de forma independente.
// ═══════════════════════════════════════════════════════════════════

/** Identificadores de página que usam o cockpit fixo (1 chave por página). */
export type FixedPageKey = 'agenda' | 'patients' | 'financial' | 'mural';

const pageFixedStorageKey = (uid: string, page: FixedPageKey) =>
  `dratento-page-fixed:${page}:${uid}`;

/** Default: TRUE (página presa, só o miolo rola). Falsy/erro → default. */
export function loadPageFixed(uid: string | null | undefined, page: FixedPageKey): boolean {
  if (!uid) return true;
  try {
    const raw = localStorage.getItem(pageFixedStorageKey(uid, page));
    if (raw == null) return true;
    return JSON.parse(raw) === true;
  } catch {
    return true;
  }
}

export function savePageFixed(
  uid: string | null | undefined,
  page: FixedPageKey,
  fixed: boolean,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(pageFixedStorageKey(uid, page), JSON.stringify(fixed));
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Densidade da interface: Conforto ↔ Compacto
// ═══════════════════════════════════════════════════════════════════

/**
 * 'compacto' recolhe a sidebar pra trilho de ícones, colapsa o cabeçalho do
 * paciente numa linha e funde as barras do topo do prontuário — devolvendo a
 * altura pro editor/visualizador. Funciona em qualquer monitor; em telas de
 * notebook pequeno (≤13") é o default quando o usuário nunca escolheu.
 */
export type ViewDensity = 'conforto' | 'compacto';

const viewDensityStorageKey = (uid: string) => `dratento-view-density:${uid}`;

/** Tela "de notebook pequeno": pouca altura útil, mas desktop (não celular). */
export function isCompactScreen(): boolean {
  try {
    return window.matchMedia('(max-height: 880px) and (min-width: 1024px)').matches;
  } catch {
    return false;
  }
}

/** Preferência salva; sem escolha explícita, cai no default pela tela. */
export function loadViewDensity(uid: string | null | undefined): ViewDensity {
  const fallback: ViewDensity = isCompactScreen() ? 'compacto' : 'conforto';
  if (!uid) return fallback;
  try {
    const raw = localStorage.getItem(viewDensityStorageKey(uid));
    if (raw === '"compacto"' || raw === '"conforto"') return JSON.parse(raw) as ViewDensity;
    return fallback;
  } catch {
    return fallback;
  }
}

export function saveViewDensity(uid: string | null | undefined, density: ViewDensity): void {
  if (!uid) return;
  try {
    localStorage.setItem(viewDensityStorageKey(uid), JSON.stringify(density));
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Seletor de valores da receita de óculos (esférico/cilíndrico)
// ═══════════════════════════════════════════════════════════════════

/**
 * Como o médico escolhe os valores de esférico e cilíndrico na receita:
 * - 'default': lista completa — grade de valores no desktop, roleta no
 *   celular (comportamento atual).
 * - 'composed': seletor composto — monta o valor em 3 escolhas (sinal,
 *   parte inteira, parte decimal), bem mais compacto. Vale pras duas
 *   telas; os demais campos (eixo, adição…) continuam na grade/roleta.
 */
export type RxPickerMode = 'default' | 'composed';

const rxPickerStorageKey = (uid: string) => `dratento-rx-picker-mode:${uid}`;

export function loadRxPickerMode(uid: string | null | undefined): RxPickerMode {
  if (!uid) return 'default';
  try {
    const raw = localStorage.getItem(rxPickerStorageKey(uid));
    return raw === 'composed' ? 'composed' : 'default';
  } catch {
    return 'default';
  }
}

export function saveRxPickerMode(
  uid: string | null | undefined,
  mode: RxPickerMode,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(rxPickerStorageKey(uid), mode);
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Modo de largura da Agenda: "amplo" (layout de desktop) ↔ "estreito"
// (layout de celular), FORÇADO pelo médico independentemente da largura
// real do navegador. Permite pré-visualizar/usar a versão estreita numa
// tela larga sem precisar redimensionar a janela.
//
// null = automático (nunca escolheu): segue a largura da tela — o
// comportamento padrão via breakpoints do Tailwind, sem regressão.
// ═══════════════════════════════════════════════════════════════════

export type AgendaWidthMode = 'wide' | 'narrow';

const agendaWidthStorageKey = (uid: string) => `dratento-agenda-width:${uid}`;

/** Lê a escolha do médico. null = automático (segue a tela). */
export function loadAgendaWidthMode(uid: string | null | undefined): AgendaWidthMode | null {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(agendaWidthStorageKey(uid));
    return raw === 'wide' || raw === 'narrow' ? raw : null;
  } catch {
    return null;
  }
}

export function saveAgendaWidthMode(
  uid: string | null | undefined,
  mode: AgendaWidthMode,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(agendaWidthStorageKey(uid), mode);
  } catch {
    /* ignora quota/serialização */
  }
}

// ═══════════════════════════════════════════════════════════════════
// Cabeçalho da ficha RECOLHIDO (uma linha: nome · idade · convênio · abas
// só com ícones) ↔ expandido. O modo foco (Shell) força o recolhido sem
// mexer nesta preferência.
// ═══════════════════════════════════════════════════════════════════

const fichaHeaderCompactStorageKey = (uid: string) => `dratento-ficha-header-compact:${uid}`;

/**
 * `padrao` vale enquanto a pessoa nunca apertou o botão (sem chave): com a
 * Nova evolução em Linhas ou Abas (spec 026) o cabeçalho nasce numa linha.
 */
export function loadFichaHeaderCompact(uid: string | null | undefined, padrao = false): boolean {
  if (!uid) return padrao;
  try {
    const v = localStorage.getItem(fichaHeaderCompactStorageKey(uid));
    return v === null ? padrao : v === '1';
  } catch {
    return padrao;
  }
}

export function saveFichaHeaderCompact(uid: string | null | undefined, compact: boolean): void {
  if (!uid) return;
  try {
    localStorage.setItem(fichaHeaderCompactStorageKey(uid), compact ? '1' : '0');
  } catch {
    /* ignora quota/serialização */
  }
}

// Mesmo toggle Amplo ↔ Estreito, mas pra FICHA DO PACIENTE (chave própria:
// o médico pode querer a Agenda ampla e a ficha estreita, ou vice-versa).
const fichaWidthStorageKey = (uid: string) => `dratento-ficha-width:${uid}`;

/** Lê a escolha do médico pra ficha. null = automático (segue a tela). */
export function loadFichaWidthMode(uid: string | null | undefined): AgendaWidthMode | null {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(fichaWidthStorageKey(uid));
    return raw === 'wide' || raw === 'narrow' ? raw : null;
  } catch {
    return null;
  }
}

export function saveFichaWidthMode(
  uid: string | null | undefined,
  mode: AgendaWidthMode,
): void {
  if (!uid) return;
  try {
    localStorage.setItem(fichaWidthStorageKey(uid), mode);
  } catch {
    /* ignora quota/serialização */
  }
}
